/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEvent, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import axios from 'axios';
import debounce from 'lodash.debounce';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import TextField from '../../components/textfield/TextField';
import {useIsPhone} from '../../hooks/useMedia';
import {TopNavSearchIcon} from '../../icons/TopNavSearchIcon';
import {dlsAlt, dlsColor, dlsLayout, dlsTypography} from '../../theme/theme';
import {BodyTypography} from '../../typography/BodyTypography';
import {mapViewGoToOptions} from '../constants';
import {
  getLocationDetails,
  getLocationlabel,
  getLocations
} from '../helpers/MapViewUtils';
import {SearchPlaceItem} from '../search/SearchPlaceItem';
import {SEARCH_ZOOM_LEVEL} from '../search/SearchView';

// defines the prop definitions for the LayerChooser
export type SearchViewProps = {
  mapView: __esri.MapView;
  geofenceType: string;
  handleSetSelectedMapPoint?: Function;
  isVisible: boolean;
  isOpeningFromBottomSheet?: boolean;
  isSave?: boolean;
  useExtent?: boolean;
  closeSearchMap?: () => void;
  useHereApi?: boolean;
};

export type SavedPlacesProps = {
  longitude: number;
  latitude: number;
  magicKey?: string;
  locationId?: string;
  text?: string;
  label?: string;
};

export type PlaceItemProps = {
  result: SavedPlacesProps;
  onClick?: (e: MouseEvent<HTMLSpanElement>) => void;
  onDeleteItem?: Function;
  isSavedPlace?: boolean;
  placeSearchText?: string;
};

const MAX_SAVED_PLACES = 5;

// function which returns the underline styles with custom border color...
const getUnderLineStyles = (borderColor = 'aaBlue') => ({
  borderBottomWidth : '1px solid !important',
  boxSizing         : 'border-box',
  borderColor       : `${dlsColor(borderColor)} !important`
});

// defines the custom styles for textfield...
export const textFieldStyles = {
  underline : {
    '&::before' : {
      ...getUnderLineStyles('grey3')
    },
    '&::after' : {
      ...getUnderLineStyles()
    },
    '&:hover' : {
      '&::before, &::after' : {
        ...getUnderLineStyles(),
        transition : 'none'
      }
    }
  },
  noPadLeft : {
    paddingLeft      : '0px !important',
    color            : 'inherit',
    fontFamily       : 'Roboto !important',
    fontStyle        : 'normal',
    fontWeight       : 400,
    fontSize         : '16px !important',
    lineHeight       : '20px !important',
    letterSpacing    : '0.5px !important',
    '&::placeholder' : {
      color         : () => `${dlsColor('grey1')}`,
      fontFamily    : 'Roboto !important',
      fontStyle     : 'normal',
      fontWeight    : 400,
      fontSize      : '13px !important',
      lineHeight    : '20px !important',
      letterSpacing : '0.5px !important'
    }
  }
};

// defines the styles used by the LayerChooser
export const useStyles = makeStyles((theme) => ({
  mapView : {
    backgroundColor : dlsColor('white'),
    top             : 0,
    maxWidth        : 414
  },
  searchIcon : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  searchMapFieldLabel : {
    color         : dlsColor('grey3'),
    marginBottom  : '8px',
    fontFamily    : 'Roboto !important',
    fontStyle     : 'normal',
    fontWeight    : 400,
    fontSize      : '14px !important',
    lineHeight    : '20px !important',
    letterSpacing : '0.5px !important',
    ...dlsTypography('body2')
  },
  mapViewMobile : {
    padding : '30px 8px 0px 8px'
  },
  searchListBox : {
    marginTop : dlsLayout('spacing')
  },
  searchList : {
    marginTop : dlsLayout('margins')
  },
  searchItem : {
    display    : 'flex',
    alignItems : 'center',
    minHeight  : '48px',
    cursor     : 'default',
    '&:hover'  : {
      backgroundColor : dlsAlt('colors', 'paleBlue2')
    }
  },
  searchItemElement : {
    paddingLeft  : dlsLayout('margins') / 2,
    paddingRight : dlsLayout('margins') / 3
  },
  ...textFieldStyles
}));

function SearchAddress({
  mapView,
  geofenceType,
  isVisible,
  closeSearchMap,
  useExtent,
  isSave,
  handleSetSelectedMapPoint,
  useHereApi = false,
  isOpeningFromBottomSheet = false
}: SearchViewProps) {
  const classes = useStyles();
  const isPhone = useIsPhone();
  const [results, setResults] = useState(null);
  const [isShowSaveButton, setIsShowSaveButton] = useState(false);
  const [savedPlaces, setSavedPlaces] = useState<SavedPlacesProps[]>(
    null
  );
  const [isMobileFullHeight, setIsMobileFullHeight] = useState(false);
  const [placeSearchText, setPlaceSearchText] = useState('');
  const currentSelectedPlace = useRef<SavedPlacesProps | null>(null);
  const sourceCancelToken = useRef(null);
  const savedPlacesRaf = useRef(null);

  async function searchLocation(val) {
    try {
      const searchText = val?.trim() || '';
      const matchedSavePlaces = savedPlacesRaf?.current?.filter(
        (place) =>
          !place?.text?.toLowerCase().indexOf(searchText?.toLowerCase())
      );
      setSavedPlaces(matchedSavePlaces);
      if (searchText) {
        const cancelToken = axios.CancelToken;
        if (typeof sourceCancelToken.current !== typeof undefined) {
          sourceCancelToken.current?.cancel();
        }
        sourceCancelToken.current = cancelToken.source();
        const suggestions = await getLocations({
          cancelToken : sourceCancelToken.current.token,
          searchText,
          useHereApi,
          mapView,
          useExtent
        });
        setResults(suggestions);
      } else {
        setResults(null);
        if (typeof sourceCancelToken.current !== typeof undefined) {
          sourceCancelToken.current?.cancel();
        }
      }
    } catch (err) {
      if (!axios.isCancel(err.error)) {
        error(err);
      }
    }
  }

  const searchOnChange = debounce(function(value) {
    setIsMobileFullHeight(true);
    setIsShowSaveButton(false);
    searchLocation(value);
    if (!value) {
      setResults(null);
    }
  }, 200);

  async function callAddress(selectedResult) {
    try {
      const selectedLocation = await getLocationDetails(selectedResult) as unknown as SavedPlacesProps;
      const {longitude, latitude}  = selectedLocation;
      mapView.zoom                 = SEARCH_ZOOM_LEVEL;
      currentSelectedPlace.current = selectedLocation;
      mapView.goTo({center: [longitude, latitude]}, mapViewGoToOptions);
      handleSetSelectedMapPoint?.({
        current : {
          longitude,
          latitude
        }
      });
    } catch (err) {
      error(err);
    }
  }

  return (
    <div className={
      isOpeningFromBottomSheet ? classes.mapViewMobile : classes.mapView
    } style={{
      display : !isVisible ? 'none' : 'inherit',
      height  :
      isMobileFullHeight && isOpeningFromBottomSheet ? 'calc(100vh - 78px)' : 'auto',
      paddingBottom : 0
    }}>
      {geofenceType !== translate('maps:geofences.draw') && (
        <BodyTypography>{translate('maps:geofences.searchLabel')}</BodyTypography>
      )}
      <div className={classes.searchListBox}>
        <TextField placeholder={`${translate('maps:geofences.placeHolder')}`}
          style={{
            root : {
              width : '100%'
            }
          }}
          InputLabelProps = {{
            classes : {
              root : classes.searchMapFieldLabel
            }
          }}
          value={placeSearchText}
          InputProps={{
            classes : {
              underline : classes.underline,
              input     : classes.noPadLeft
            },
            startAdornment : (
              <div className={classes.searchIcon}>
                <TopNavSearchIcon />
              </div>
            )
          }}
          onChange={function({target: {value}}) {
            setPlaceSearchText(value);
            searchOnChange(value);
          }} />
      </div>
      {(!!results?.length || !!savedPlaces?.length) && (
        <div className={classes.searchList}
          style={{
            overflow  : 'auto',
            maxHeight : (isOpeningFromBottomSheet || isPhone)
              ? 'calc(100vh - 200px)'
              : mapView.height - 200
          }}>

          {results?.map((result) => {
            const {magicKey, locationId, text} = result;
            const searchText = getLocationlabel(text, useHereApi);
            return (
              <SearchPlaceItem key={magicKey || locationId}
                placeSearchText={placeSearchText}
                result={result}
                onClick={function() {
                  if (savedPlacesRaf.current?.length < MAX_SAVED_PLACES) {
                    setIsShowSaveButton(true);
                  }
                  const selectedResult = result;
                  callAddress(selectedResult);
                  setPlaceSearchText(searchText);
                  setResults(null);
                  setSavedPlaces(null);
                  setIsMobileFullHeight(false);
                }} />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SearchAddress;
